/**
 * Open an in-app (SPA-relative) path in a new tab.
 *
 * Inside the UI shell this opens a shell tab (deduped: an already-open path
 * is focused) through the opener `ShellProvider` registers (D10). Outside the
 * shell, or for a path the shell does not host, it opens a new browser tab.
 * ``noopener,noreferrer`` severs ``window.opener`` and strips the Referer.
 */
import type React from 'react';

type ShellOpener = (path: string) => boolean;

let shellOpener: ShellOpener | null = null;

/** Registered by `ShellProvider` on mount, cleared on unmount. The opener returns true when it handled the path. */
export function setShellOpener(opener: ShellOpener | null): void {
  shellOpener = opener;
}

/** True inside the UI shell: a tab opened after an await is not popup-blocked there. */
export function hasShellOpener(): boolean {
  return shellOpener !== null;
}

export function openInNewTab(path: string): void {
  if (shellOpener && path.startsWith('/') && !path.startsWith('//') && shellOpener(path)) return;
  window.open(path, '_blank', 'noopener,noreferrer');
}

/**
 * `onClick` for an `<a href={path} target="_blank">` that points at an in-app
 * path: a plain left click opens it through `openInNewTab` (a shell tab inside
 * the shell). Modified/middle clicks and non-SPA hrefs are left to the browser,
 * which is why the anchor keeps its `href`.
 */
export function openInAppLink(e: React.MouseEvent, path: string): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  if (!path.startsWith('/') || path.startsWith('//')) return;
  e.preventDefault();
  openInNewTab(path);
}
